import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { type ReactNode } from "react";
import {
  OrganizationMessageStatus,
  type OrganizationMessageFieldsFragment,
  useOrganizationMessageDetailQuery,
  useOrganizationMessageReachQuery,
} from "../../../generated/graphql";
import {
  audienceFromMessage,
  audienceInput,
  formatDateTime,
  reachSummary,
} from "../message-content";
import DeliveryStats from "./delivery-stats";
import MessageActions from "./message-actions";
import { SeverityChip, StatusChip } from "./message-chips";
import MessageHistory from "./message-history";
import MessagePreview from "./message-preview";

const styles = {
  panel: { p: 3 },
  facts: {
    display: "grid",
    gridTemplateColumns: "140px minmax(0, 1fr)",
    columnGap: 2,
    rowGap: 1.5,
    alignItems: "baseline",
  },
  label: { color: "text.secondary", fontSize: 14 },
  companies: { flexWrap: "wrap", gap: 0.5 },
  section: { fontWeight: 600, mb: 1 },
} satisfies Record<string, SxProps<Theme>>;

const Fact = ({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) => (
  <>
    <Typography sx={styles.label}>{label}</Typography>
    <Box>{children}</Box>
  </>
);

const expiryText = (message: OrganizationMessageFieldsFragment): string => {
  if (message.usesDefaultExpiry && message.expiresAt === null) {
    return "Severity default, counted from when it starts";
  }
  return message.expiresAt === null
    ? "Never"
    : formatDateTime(message.expiresAt);
};

type MessageDetailProps = {
  readonly uuid: string;
  readonly onEdit: (message: OrganizationMessageFieldsFragment) => void;
};

/** One message: how it looks, who gets it, delivery, actions and history. */
const MessageDetail = ({ uuid, onEdit }: MessageDetailProps) => {
  const { data, loading, error } = useOrganizationMessageDetailQuery({
    variables: { uuid },
    errorPolicy: "all",
  });
  const message = data?.organizationMessage;
  const isDraft = message?.status === OrganizationMessageStatus.Draft;
  const { data: reachData, error: reachError } =
    useOrganizationMessageReachQuery({
      variables: {
        audience: audienceInput(
          message === undefined || message === null
            ? { allCompanies: false, companies: [] }
            : audienceFromMessage(message),
        ),
      },
      skip: !isDraft,
      errorPolicy: "all",
    });

  if (loading && message === undefined) {
    return <CircularProgress />;
  }
  if (message === undefined || message === null) {
    return (
      <Alert severity="error">{error?.message ?? "Message not found."}</Alert>
    );
  }
  const reach = reachData?.organizationMessageReach;

  return (
    <Paper variant="outlined" sx={styles.panel}>
      <Stack spacing={3}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SeverityChip severity={message.severity} />
            <StatusChip message={message} />
          </Stack>
          <MessageActions
            message={message}
            reach={reach}
            onEdit={() => {
              onEdit(message);
            }}
          />
          {isDraft && reachError !== undefined && (
            <Alert severity="warning">
              Could not count who it reaches, so sending is disabled:{" "}
              {reachError.message}
            </Alert>
          )}
        </Stack>
        <MessagePreview
          severity={message.severity}
          title={message.title}
          body={message.body}
          linkLabel={message.linkLabel ?? null}
        />
        <Divider />
        <Box sx={styles.facts}>
          <Fact label="Sent to">
            <Stack direction="row" sx={styles.companies}>
              {message.allCompanies ? (
                <Chip size="small" color="primary" label="All companies" />
              ) : (
                message.companies.map((company) => (
                  <Chip key={company.uuid} size="small" label={company.name} />
                ))
              )}
            </Stack>
          </Fact>
          {isDraft && reach !== undefined && (
            <Fact label="Would reach">
              <Typography variant="body2">
                {reachSummary(message.severity, reach)}
              </Typography>
            </Fact>
          )}
          <Fact label="Starts">
            <Typography variant="body2">
              {message.startsAt === null
                ? "When it is sent"
                : formatDateTime(message.startsAt)}
            </Typography>
          </Fact>
          <Fact label="Stops showing">
            <Typography variant="body2">{expiryText(message)}</Typography>
          </Fact>
          {message.linkType !== null && (
            <Fact label="Link">
              <Typography variant="body2">
                {message.linkLabel}: {message.linkTarget}
              </Typography>
            </Fact>
          )}
        </Box>
        {!isDraft && <DeliveryStats uuid={uuid} />}
        <MessageHistory history={data?.organizationMessageHistory ?? []} />
      </Stack>
    </Paper>
  );
};

export default MessageDetail;
